// Miky Motorsport - RaceManager (M2): state machine, multi-car, kola, pozice, casomira
import { Vehicle } from './physics/vehicle.js';
import { AIController } from './ai.js';
import { stepWorld } from './physics/world.js';
import { clamp } from './physics/tires.js';
import { CAR_MODEL_KEYS } from './render/carModels.js';
import { orderAiDefs, firstCpAfter, rankCars, rubberScale, pickEliminated, raceDone, advanceCheckpoint } from './raceLogic.js';
import { sectorBoundaries } from './sectors.js';

export const RACE_STATE = Object.freeze({
  COUNTDOWN: 'COUNTDOWN',
  RACING: 'RACING',
  FINISHED: 'FINISHED',
});

// Drzeni auta na miste (grid pred startem / stihaci hold hrace). Vehicle controls jen cte,
// takze sdilena zmrazena reference je bezpecna.
const HOLD_CONTROLS = Object.freeze({ throttle: 0, brake: 1, steer: 0, handbrake: true });

const AI_DEFS = [
  { name: 'Falko', color: 0x3aa0ff, skill: 0.95 },
  { name: 'Brutus', color: 0x9b59b6, skill: 0.9 },
  { name: 'Vento', color: 0x2ecc71, skill: 0.97 },
  { name: 'Apex', color: 0xf1c40f, skill: 0.93 },
  { name: 'Dust', color: 0xe67e22, skill: 0.88 },
];

export class RaceManager {
  constructor(world, track, sceneMgr, carSpec, opts = {}) {
    this.world = world;
    this.track = track;
    this.sceneMgr = sceneMgr;
    this.spec = carSpec;
    // Rezim zavodu: 'circuit' (klasika) | 'sprint' (1 kolo) | 'duel' (1v1) |
    //   'elimination' (kazdych X s vypadne posledni) | 'pursuit' (hrac start se zpozdenim).
    this.mode = opts.mode || 'circuit';
    let reqAI = opts.numAI ?? 4;
    if (this.mode === 'duel') reqAI = 1;
    this.numAI = Math.min(reqAI, track.gridPositions.length - 1, AI_DEFS.length);

    this.state = RACE_STATE.COUNTDOWN;
    this.countdown = 4.0;
    this.raceTime = 0;
    this._finishGrace = 15; // strop (s) po 1. dojezdu (fallback kdyz hrac nedojede - DNF; velkorysy pro sprint/pursuit)
    // Eliminace (KOLOVA): po dokonceni kola vedoucim vypadne posledni. _elimLapMark = posledni
    // kolo vedouciho, na ktere uz eliminace probehla (aby se vyradilo jen 1 auto za kolo).
    this._elimLapMark = 0;
    this._elimCount = 0;
    // Stihaci zavod: hrac drzen na startu (AI maji naskok).
    this.pursuitHold = this.mode === 'pursuit' ? 2.8 : 0;
    this.firstFinishTime = null; // cas prvniho dojezdu (pro odstupy v leaderboardu)
    this.timeTrial = !!opts.timeTrial; // solo jizda na cas (bez souperu)
    this.daily = !!opts.daily; // denni vyzva (J) - honis rivaluv ghost na cas
    this.ttPB = opts.ttPB ?? null; // predchozi osobni rekord (ms)
    this.cars = [];

    // Obtiznost (M4): skaluje tempo/skill AI a rubber-band. null = puvodni chovani.
    const diff = opts.difficulty || null;
    this._rubberK = diff ? diff.rubberK : 0.0006;
    this._aiPace = diff ? diff.pace : 1.0;
    this._aiThrottle = diff ? (diff.throttle ?? 1.0) : 1.0;
    // B3: hranice sektoru (cp indexy) pro casomiru sektoru hrace.
    this._sectorBnd = sectorBoundaries(track.numCheckpoints, 3);
    // Pocet kol pro tento zavod (event muze prepsat default trate).
    this.laps = opts.laps ?? track.laps;
    if (this.mode === 'sprint') this.laps = 1;
    // Eliminace: kolova (5 aut / 4 kola). Po kazdem kole vedouciho vypadne posledni, dokud
    // nezbydou 2 -> posledni kolo je duel. Pocet kol = z eventu (default 4 v events.js).
    if (this.mode === 'elimination') this.laps = opts.laps ?? Math.max(track.laps, 4);
    // Tvrdy strop celkoveho casu zavodu (s) - bezpecnostni pojistka proti softlocku,
    // kdyz NIKDO nedojede (firstFinishTime zustane null, takze finish-grace se nespusti).
    // Velkorysy: 180 s na kolo + 60 s rezerva, daleko za realnym dojezdem.
    this._maxRaceTime = 60 + this.laps * 180;

    // Poradi AI definic - pripadny rival (z eventu) na celo startovniho pole.
    const defs = orderAiDefs(AI_DEFS, opts.rival);

    this.playerSpec = opts.playerSpec || carSpec;
    const grid = track.gridPositions;
    // Rozdeleni 3D modelu: hrac ma svuj, AI dostanou ruzne (jako v reference)
    const playerModel = this.playerSpec.model || 'q1';
    const aiModels = CAR_MODEL_KEYS.filter((k) => k !== playerModel);
    // Hrac (zadni rada gridu - aby mel koho predjizdet)
    this._addCar({ grid: grid[this.numAI], isPlayer: true, name: 'Miky', color: this.playerSpec.color, spec: this.playerSpec, modelKey: playerModel });
    // AI (predni rada). Cela na poli = rychlejsi (zaporny i = vyssi skill).
    for (let i = 0; i < this.numAI; i++) {
      const d = defs[i];
      // Pod obtiznosti pouzij skill presetu + maly rozptyl (cela pole rychlejsi), jinak skill z definice.
      let skill = diff ? clamp(diff.aiSkill + ((this.numAI - 1) / 2 - i) * 0.02, 0.5, 1) : d.skill;
      // Rival je vyzva navic - mensi skill boost.
      const isRival = opts.rival && d.name === opts.rival;
      if (isRival) skill = clamp(skill + 0.05, 0.5, 1);
      this._addCar({
        grid: grid[i], isPlayer: false, name: d.name, color: d.color, isRival,
        skill, pace: this._aiPace, throttle: this._aiThrottle,
        laneOffset: (i % 2 === 0 ? -1.5 : 1.5),
        modelKey: aiModels[i % aiModels.length],
      });
    }
    this.player = this.cars.find((c) => c.isPlayer);
    // AI prevezme rizeni hracova auta po dojezdu (auto dal jede po okruhu, hrac uz neridi).
    this.playerAI = new AIController(this.player.vehicle, this.track, {
      skill: 0.8, pace: this._aiPace, throttle: this._aiThrottle,
    });
  }

  _addCar({ grid, isPlayer, name, color, skill, pace, throttle, laneOffset, spec, isRival, modelKey }) {
    const useSpec = spec ? { ...spec, color: color ?? spec.color } : { ...this.spec, color };
    const vehicle = new Vehicle(this.world, useSpec, grid.pos, grid.heading);
    const built = this.sceneMgr.buildCar(useSpec, modelKey);
    this.sceneMgr.add(built.group);
    const ai = isPlayer ? null : new AIController(vehicle, this.track, { skill, pace, throttle, laneOffset });
    const curr = vehicle.getState();
    this.cars.push({
      vehicle, ai, isPlayer, name, color, isRival: !!isRival,
      group: built.group, wheels: built.wheels, brakeMat: built.brakeMat, isModel: built.isModel,
      prev: curr, curr,
      lap: 0, nextCp: 0, started: false, _sidePrev: undefined,
      lapStartTime: 0, lapTimes: [], bestLap: Infinity,
      _secSplits: [], lastSectors: null, bestSectors: null, // B3: sektory
      finished: false, finishTime: 0, position: 1, progress: 0,
      eliminated: false, _elimAt: 0,
    });
  }

  update(dt, playerControls) {
    if (this.state === RACE_STATE.COUNTDOWN) {
      this.countdown -= dt;
      if (this.countdown <= 0) { this.state = RACE_STATE.RACING; this.raceTime = 0; }
    }
    const racing = this.state === RACE_STATE.RACING;

    // Controls pro vsechna auta
    for (const car of this.cars) {
      car.vehicle.surfaceGrip = this.track.surfaceGripAt(car.curr.pos.x, car.curr.pos.z);
      let controls;
      if (!racing) {
        controls = HOLD_CONTROLS; // drzeni na gridu
      } else if (car.isPlayer && !car.finished) {
        // stihaci zavod: drz hrace na startu, AI maji naskok
        if (this.mode === 'pursuit' && this.raceTime < this.pursuitHold) {
          controls = HOLD_CONTROLS;
        } else {
          controls = playerControls;
        }
      } else {
        // dojeta auta (vc. hrace) jedou dal po okruhu pod AI - zadne stani na trati
        const ctrl = car.isPlayer ? this.playerAI : car.ai;
        controls = ctrl.computeControls(dt);
      }
      car.vehicle.applyInput(controls);
    }

    // Fyzika (vsechna auta v jednom kroku)
    stepWorld(this.world, (sdt) => {
      for (const car of this.cars) car.vehicle.update(sdt);
    });

    // Snapshoty pro interpolaci
    for (const car of this.cars) {
      car.prev = car.curr;
      car.curr = car.vehicle.getState();
      // reset pri padu - vc. race-tracking stavu (jinak desync casomiry/pozic)
      if (car.curr.pos.y < -6) {
        this.resetCar(car);
        car.curr = car.vehicle.getState();
        car.prev = car.curr;
      }
    }

    if (racing) {
      this.raceTime += dt;
      for (const car of this.cars) this._trackCar(car);
      // stuck AI (stoji >4 s) -> reset na trat (anti-zaseknuti)
      for (const car of this.cars) {
        if (car.finished || !car.ai) continue;
        if (Math.abs(car.vehicle.speedMs) < 1) car._stuckT = (car._stuckT || 0) + dt;
        else car._stuckT = 0;
        if (car._stuckT > 4) { this.resetCarToTrack(car); car._stuckT = 0; }
      }
      // prevracene auto (na strese/boku) -> po ~2 s auto-narovnani na trat (vc. hrace).
      // up.y rotovaneho (0,1,0) = 1 - 2(x^2 + z^2); < 0.4 = vyrazny naklon.
      for (const car of this.cars) {
        // Stihaci start: hrac je zamerne drzen na miste (na prudke trati muze mit
        // naklon < 0.4) - nepocitej mu flip timer, jinak by byl falesne teleportovan.
        const held = car.isPlayer && this.mode === 'pursuit' && this.raceTime < this.pursuitHold;
        if (held) { car._flipT = 0; continue; }
        const r = car.curr.rot;
        const upY = 1 - 2 * (r.x * r.x + r.z * r.z);
        if (upY < 0.4) car._flipT = (car._flipT || 0) + dt;
        else car._flipT = 0;
        if (car._flipT > 2.0) { this.resetCarToTrack(car); car._flipT = 0; }
      }
      this._rankAndRubber();

      // Eliminace (KOLOVA): po dokonceni kola vedoucim vypadne posledni aktivni auto.
      // Az po _rankAndRubber, aby byl progress/poradi cerstvy (pickEliminated cte progress).
      if (this.mode === 'elimination') this._maybeEliminateOnLap();

      // Konec zavodu dle rezimu.
      const done = raceDone({
        mode: this.mode,
        playerFinished: this.player.finished,
        playerEliminated: this.player.eliminated,
        allFinished: this.cars.every((c) => c.finished),
        activeLeft: this.cars.filter((c) => !c.eliminated && !c.finished).length,
        elimOccurred: this._elimCount > 0,
        firstFinishTime: this.firstFinishTime,
        raceTime: this.raceTime,
        finishGrace: this._finishGrace,
        maxRaceTime: this._maxRaceTime,
      });
      if (done) this.state = RACE_STATE.FINISHED;
    }
  }

  // Reset auta vc. race-tracking stavu (pad / manualni T). Prepocita nextCp ze spawn pozice.
  resetCar(car) {
    car.vehicle.reset(this.track.spawn, this.track.heading);
    this.resetCarTracking(car);
  }

  // Reset zaseknuteho auta na NEJBLIZSI bod trati (neztrati progres, jen se odlepi).
  resetCarToTrack(car) {
    const t = car.curr.pos;
    const near = this.track.nearest(t.x, t.z);
    const p = this.track.pointAtS(near.s);
    const gy = this.track.groundY ? this.track.groundY(p.x, p.z) : 0;
    car.vehicle.reset({ x: p.x, y: gy + 1.2, z: p.z }, Math.atan2(p.tx, p.tz));
    car._sidePrev = undefined;
    // Prepocet nextCp z reset pozice (near.s) - reset mohl auto posunout pres hranici
    // checkpointu; jinak by nextCp zustal stale -> chybny pocet kol/checkpointu.
    // (lapStartTime zde nemenime - tohle je odlepeni bez ztraty progresu.)
    car.nextCp = firstCpAfter(this.track.checkpoints, near.s);
    car.curr = car.vehicle.getState();
    car.prev = car.curr;
  }

  resetCarTracking(car) {
    car._sidePrev = undefined;
    const near = this.track.nearest(this.track.spawn.x, this.track.spawn.z);
    car.nextCp = firstCpAfter(this.track.checkpoints, near.s);
    car.lapStartTime = this.raceTime; // at se nezapocita absurdni kolo
  }

  // Tenky wrapper nad cistou advanceCheckpoint (js/raceLogic.js). `car` ma primo vsechna
  // pole, ktera advanceCheckpoint cte/zapisuje (nextCp, _sidePrev, started, lapStartTime,
  // lap, lapTimes, bestLap, finished, finishTime), takze ho predame jako `state`. Wrapper
  // jen doplni firstFinishTime (cas prvniho dojezdu) pri prvnim dojezdu.
  // Lateralni okno = fyzicky koridor az ke stene (BARRIER_OFF ~16 m od osy): pokryje vyjeti/
  // smyk do runoffu, ale uz NEzapocita absurdni prejezdy daleko mimo trat -> nafouknute kola.
  // Dojeta ANI vyrazena (eliminace) auta uz nezapocitavaji kola/dojezd [F-405/F-404].
  _trackCar(car) {
    const { justFinished } = advanceCheckpoint(
      car, this.track.checkpoints, car.curr.pos.x, car.curr.pos.z,
      this.raceTime, this.track.numCheckpoints, this.laps, car.eliminated,
      car.isPlayer ? this._sectorBnd : null, // B3: sektory jen pro hrace
    );
    if (justFinished && this.firstFinishTime == null) this.firstFinishTime = car.finishTime;
  }

  // Eliminace (KOLOVA): kdyz vedouci dokonci kolo, vypadne posledni aktivni auto. Vyrazuje
  // se DOKUD nezbydou 2 auta -> posledni kolo je vzdy DUEL. (5 aut / 4 kola: po dokonceni
  // kol 1,2,3 vypadnou 3 auta, do posledniho kola jdou 2.) Vazano na kolo, ne na cas.
  _maybeEliminateOnLap() {
    let leaderLap = 0;
    for (const c of this.cars) if (c.lap > leaderLap) leaderLap = c.lap;
    if (leaderLap <= this._elimLapMark) return; // vedouci jeste nedokoncil dalsi kolo
    this._elimLapMark = leaderLap;
    const active = this.cars.filter((c) => !c.eliminated && !c.finished).length;
    if (active > 2) this._eliminateLast(); // pod 2 uz nevyrazuj (finale = duel)
  }

  // Eliminace: vyrad posledni aktivni auto (nejnizsi progress).
  _eliminateLast() {
    const last = pickEliminated(this.cars);
    if (!last) return;
    last.eliminated = true;
    last._elimAt = ++this._elimCount;
  }

  _rankAndRubber() {
    const L = this.track.totalLength;
    for (const car of this.cars) {
      if (!car.finished) {
        const near = this.track.nearest(car.curr.pos.x, car.curr.pos.z);
        car.progress = car.lap * L + near.s;
      }
    }
    rankCars(this.cars);

    for (const car of this.cars) {
      if (car.isPlayer || !car.ai) continue;
      car.ai.setRubber(rubberScale(this.player.progress, car.progress, this._rubberK, this.player.finished));
    }
  }

  // pro HUD (vc. odstupu za prvnim dojezdem)
  results() {
    return [...this.cars].sort((a, b) => a.position - b.position).map((c) => ({
      name: c.name, position: c.position, isPlayer: c.isPlayer, isRival: c.isRival,
      bestLap: c.bestLap === Infinity ? null : c.bestLap,
      finished: c.finished, eliminated: c.eliminated,
      finishTime: c.finished ? c.finishTime : null,
      gap: c.finished && this.firstFinishTime != null ? c.finishTime - this.firstFinishTime : null,
      color: c.color,
    }));
  }

  get anyFinished() { return this.firstFinishTime != null; }

  dispose() {
    for (const car of this.cars) {
      this.sceneMgr.remove(car.group);
      this.sceneMgr.disposeObject(car.group);
      // odstran i fyzikalni telo z Rapier sveta (jinak se kazde auto dal simuluje navzdy)
      car.vehicle.dispose();
    }
  }
}
